import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { Send, RefreshCw, Bell, Users, CheckCircle2, XCircle, Clock, ChevronLeft, ChevronRight, Bookmark, BookmarkPlus, Trash2 } from 'lucide-react';

interface NotificationTemplate {
  id: string;
  name: string;
  title: string;
  body: string;
  screen: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

interface NotificationLog {
  id: string;
  sent_by: string;
  title: string;
  body: string;
  target_type: 'all' | 'specific';
  target_count: number | null;
  success_count: number | null;
  failure_count: number | null;
  delivered_count: number | null;
  device_failure_count: number | null;
  failure_summary: Record<string, number> | null;
  sent_at: string;
  receipts_checked_at: string | null;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) +
    ', ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
}

const MAX_TITLE_CHARS = 65;
const MAX_BODY_CHARS = 240;

// Sentinel dropdown values that aren't a real in-app screen path — kept distinct from
// `''` (no deep link) and from an actual `screen` string so the component can tell
// "nothing picked", "Play Store", and "type your own URL" apart before building the
// request payload.
const CUSTOM_URL_OPTION = '__custom_url__';
const PLAY_STORE_OPTION = 'external:play-store';

/** A `screen` value the backend will accept for `Linking.openURL()` — http(s) only. */
function isValidExternalUrl(value: string): boolean {
  if (!/^https?:\/\//i.test(value.trim())) return false;
  try {
    const parsed = new URL(value.trim());
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * HQ Notifications page.
 *
 * Lets admins compose and send push notifications to all users or a
 * selected subset, and view history with on-demand delivery receipt checks.
 */
export default function HQNotifications() {
  const { getToken, logout, currentUser } = useAuth();

  // --- Compose form state ---
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [targetType, setTargetType] = useState<'all' | 'specific'>('all');
  // '' = no deep link, PLAY_STORE_OPTION / CUSTOM_URL_OPTION = sentinels, anything else
  // = an in-app screen path straight from `deepLinkScreens`.
  const [screenSelection, setScreenSelection] = useState('');
  const [customUrl, setCustomUrl] = useState('');
  const [deepLinkScreens, setDeepLinkScreens] = useState<{ label: string; value: string }[]>([]);
  const [playStoreSentinel, setPlayStoreSentinel] = useState(PLAY_STORE_OPTION);
  const [allUsers, setAllUsers] = useState<{ id: string; display_name: string | null; email: string | null }[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [usersLoading, setUsersLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<{ successCount: number; failureCount: number; targetCount: number } | null>(null);
  const [sendError, setSendError] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);

  // --- History state ---
  const [notifications, setNotifications] = useState<NotificationLog[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // --- Receipt check state ---
  const [checkingReceiptId, setCheckingReceiptId] = useState<string | null>(null);

  // --- Template state ---
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [templatesError, setTemplatesError] = useState('');
  const [showSaveTemplateForm, setShowSaveTemplateForm] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [saveTemplateError, setSaveTemplateError] = useState('');
  const [deletingTemplateId, setDeletingTemplateId] = useState<string | null>(null);

  // Fetch paginated notification history
  const fetchHistory = useCallback(async (p = 1) => {
    setHistoryLoading(true);
    setHistoryError('');
    try {
      const token = await getToken();
      if (!token) return;

      const res = await fetch(`/api/v1/hq/notifications?page=${p}&limit=10`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Failed to fetch');

      setNotifications(json.data.notifications);
      setTotal(json.data.total);
      setTotalPages(json.data.totalPages);
      setPage(p);
    } catch (err: any) {
      setHistoryError(err.message);
    } finally {
      setHistoryLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    fetchHistory(1);
  }, [fetchHistory]);

  // Fetch the allowlisted in-app screens once — sourced from the same
  // `validateNotificationScreen()` list the backend enforces, so this dropdown can never
  // drift out of sync with what the server will actually accept.
  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        if (!token) return;
        const res = await fetch('/api/v1/hq/notifications/screens', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json();
        if (!res.ok || !json.success) throw new Error(json.error || 'Failed to fetch screens');
        setDeepLinkScreens(json.data.screens);
        if (json.data.playStoreSentinel) setPlayStoreSentinel(json.data.playStoreSentinel);
      } catch (err: any) {
        console.error('HQNotifications: failed to load deep link screens', err);
      }
    })();
  }, [getToken]);

  const fetchTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    setTemplatesError('');
    try {
      const token = await getToken();
      if (!token) return;

      const res = await fetch('/api/v1/hq/notifications/templates', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Failed to fetch templates');

      setTemplates(json.data.templates);
    } catch (err: any) {
      setTemplatesError(err.message);
    } finally {
      setTemplatesLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Fetch users when targetType = 'specific', debounced on userSearch
  useEffect(() => {
    if (targetType !== 'specific') return;
    const debounceTimer = setTimeout(async () => {
      setUsersLoading(true);
      try {
        const token = await getToken();
        if (!token) return;
        const params = new URLSearchParams({ limit: '100' });
        if (userSearch.trim()) params.set('search', userSearch.trim());
        const res = await fetch(`/api/v1/hq/users?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json();
        if (!res.ok || !json.success) throw new Error(json.error || 'Failed to fetch users');
        setAllUsers(json.data.users);
      } catch (err: any) {
        console.error('HQNotifications: failed to load users', err);
      } finally {
        setUsersLoading(false);
      }
    }, 300);
    return () => clearTimeout(debounceTimer);
  }, [targetType, userSearch, getToken]);

  // The value that will actually be sent as `screen` — resolves the custom-URL sentinel
  // to the typed URL, otherwise passes the selection straight through.
  const resolvedScreen = screenSelection === CUSTOM_URL_OPTION ? customUrl.trim() : screenSelection;

  // Prefills the compose form from a saved template. Deliberately leaves target/userIds
  // untouched — a template only carries content, the admin still picks the audience.
  const handleUseTemplate = (t: NotificationTemplate) => {
    setTitle(t.title);
    setBody(t.body);
    if (!t.screen) {
      setScreenSelection('');
      setCustomUrl('');
    } else if (t.screen === playStoreSentinel) {
      setScreenSelection(playStoreSentinel);
      setCustomUrl('');
    } else if (isValidExternalUrl(t.screen)) {
      setScreenSelection(CUSTOM_URL_OPTION);
      setCustomUrl(t.screen);
    } else {
      setScreenSelection(t.screen);
      setCustomUrl('');
    }
    setSendResult(null);
    setSendError('');
  };

  const handleSaveTemplate = async () => {
    setSavingTemplate(true);
    setSaveTemplateError('');
    try {
      const token = await getToken();
      if (!token) return;

      const payload: any = { name: newTemplateName.trim(), title, body };
      if (resolvedScreen) payload.screen = resolvedScreen;

      const res = await fetch('/api/v1/hq/notifications/templates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Failed to save template');

      setTemplates(prev => [json.data, ...prev]);
      setShowSaveTemplateForm(false);
      setNewTemplateName('');
    } catch (err: any) {
      setSaveTemplateError(err.message);
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleDeleteTemplate = async (id: string) => {
    setDeletingTemplateId(id);
    try {
      const token = await getToken();
      if (!token) return;

      const res = await fetch(`/api/v1/hq/notifications/templates/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Failed to delete template');

      setTemplates(prev => prev.filter(t => t.id !== id));
    } catch (err: any) {
      console.error('HQNotifications: failed to delete template', err);
    } finally {
      setDeletingTemplateId(null);
    }
  };

  const handleSend = async () => {
    setSending(true);
    setSendError('');
    setSendResult(null);
    setShowConfirm(false);

    try {
      const token = await getToken();
      if (!token) return;

      const payload: any = { title, body, targetType };
      if (targetType === 'specific') payload.userIds = selectedUserIds;
      if (resolvedScreen) payload.screen = resolvedScreen;

      const res = await fetch('/api/v1/hq/notifications/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Send failed');

      setSendResult(json.data);
      setTitle('');
      setBody('');
      setSelectedUserIds([]);
      setUserSearch('');
      setTargetType('all');
      setScreenSelection('');
      setCustomUrl('');
      fetchHistory(1);
    } catch (err: any) {
      setSendError(err.message);
    } finally {
      setSending(false);
    }
  };

  const handleCheckReceipts = async (notificationId: string) => {
    setCheckingReceiptId(notificationId);
    try {
      const token = await getToken();
      if (!token) return;

      const res = await fetch(`/api/v1/hq/notifications/${notificationId}/check-receipts`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Receipt check failed');

      fetchHistory(page);
    } catch (err: any) {
      console.error('HQNotifications: receipt check failed', err);
    } finally {
      setCheckingReceiptId(null);
    }
  };

  const toggleUser = (id: string) => {
    setSelectedUserIds(prev =>
      prev.includes(id) ? prev.filter(u => u !== id) : [...prev, id]
    );
  };

  const recipientCount = targetType === 'all'
    ? 'all users'
    : `${selectedUserIds.length} user${selectedUserIds.length !== 1 ? 's' : ''}`;

  const customUrlError = screenSelection === CUSTOM_URL_OPTION && customUrl.trim().length > 0 && !isValidExternalUrl(customUrl)
    ? 'Must be a valid http:// or https:// URL'
    : '';

  const canSend = title.trim().length > 0 &&
    body.trim().length > 0 &&
    (targetType === 'all' || selectedUserIds.length > 0) &&
    (screenSelection !== CUSTOM_URL_OPTION || isValidExternalUrl(customUrl));

  const canSaveTemplate = newTemplateName.trim().length > 0 &&
    title.trim().length > 0 &&
    body.trim().length > 0 &&
    (screenSelection !== CUSTOM_URL_OPTION || isValidExternalUrl(customUrl));

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex">
      {/* Sidebar */}
      <div className="w-64 border-r border-zinc-800 p-6 flex flex-col gap-8">
        <div className="font-bold text-xl text-yellow-400">HQ AppPulse</div>

        <nav className="flex flex-col gap-2 flex-grow">
          <Link to="/hq/dashboard" className="flex items-center gap-3 px-4 py-3 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/50 rounded-xl transition-colors">
            Dashboard
          </Link>
          <Link to="/hq/insights" className="flex items-center gap-3 px-4 py-3 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/50 rounded-xl transition-colors">
            User Insights
          </Link>
          <Link to="/hq/transactions" className="flex items-center gap-3 px-4 py-3 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/50 rounded-xl transition-colors">
            Transactions
          </Link>
          <Link to="/hq/notifications" className="flex items-center gap-3 px-4 py-3 bg-zinc-900 text-yellow-400 rounded-xl font-medium">
            Notifications
          </Link>
        </nav>

        <div className="flex flex-col gap-4 border-t border-zinc-800 pt-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-zinc-800 border border-zinc-700"></div>
            <div className="overflow-hidden">
              <p className="text-sm font-medium text-zinc-100 truncate">{currentUser?.displayName || 'Admin'}</p>
              <p className="text-xs text-zinc-500 truncate">{currentUser?.email}</p>
            </div>
          </div>
          <button
            onClick={logout}
            className="w-full px-4 py-2 text-sm text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900 rounded-xl transition-colors text-left"
          >
            Sign out
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 p-8 overflow-auto">
        <div className="max-w-3xl mx-auto flex flex-col gap-8">

          {/* Header */}
          <div>
            <h1 className="text-2xl font-bold text-zinc-100">Push Notifications</h1>
            <p className="text-zinc-400 mt-1 text-sm">Compose and send reminders to your users.</p>
          </div>

          {/* Templates card */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-zinc-100">Templates</h2>
              {!showSaveTemplateForm && (
                <button
                  onClick={() => setShowSaveTemplateForm(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 border border-zinc-700 rounded-lg transition-colors"
                >
                  <BookmarkPlus size={13} /> Save current as template
                </button>
              )}
            </div>

            {showSaveTemplateForm && (
              <div className="flex flex-col gap-2 bg-zinc-800 border border-zinc-700 rounded-xl p-4">
                <label className="text-sm text-zinc-400">Template name</label>
                <input
                  type="text"
                  value={newTemplateName}
                  onChange={e => setNewTemplateName(e.target.value)}
                  placeholder="e.g. Weekly nudge"
                  className="bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-2.5 text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-yellow-400/30 text-sm"
                />
                <p className="text-xs text-zinc-500">
                  Saves the current title, message, and deep link above. Target audience is not saved — you'll pick it again each time you use this template.
                </p>
                {saveTemplateError && <p className="text-xs text-red-400">{saveTemplateError}</p>}
                <div className="flex gap-3">
                  <button
                    onClick={handleSaveTemplate}
                    disabled={!canSaveTemplate || savingTemplate}
                    className="flex items-center gap-2 px-4 py-2 bg-yellow-400 text-zinc-900 rounded-xl text-sm font-semibold hover:bg-yellow-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    {savingTemplate ? 'Saving...' : 'Save template'}
                  </button>
                  <button
                    onClick={() => { setShowSaveTemplateForm(false); setNewTemplateName(''); setSaveTemplateError(''); }}
                    className="px-4 py-2 text-sm text-zinc-400 hover:text-zinc-100 hover:bg-zinc-700 rounded-xl transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {templatesLoading ? (
              <p className="text-xs text-zinc-500">Loading templates...</p>
            ) : templatesError ? (
              <p className="text-xs text-red-400">{templatesError}</p>
            ) : templates.length === 0 ? (
              <p className="text-xs text-zinc-500">No saved templates yet.</p>
            ) : (
              <div className="flex flex-col gap-2">
                {templates.map(t => (
                  <div key={t.id} className="flex items-center justify-between gap-3 px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl">
                    <div className="flex items-center gap-3 overflow-hidden">
                      <Bookmark size={14} className="text-yellow-400 flex-shrink-0" />
                      <div className="overflow-hidden">
                        <p className="text-sm font-medium text-zinc-100 truncate">{t.name}</p>
                        <p className="text-xs text-zinc-500 truncate">{t.title}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => handleUseTemplate(t)}
                        className="px-3 py-1.5 text-xs text-yellow-400 hover:bg-yellow-400/10 border border-yellow-400/30 rounded-lg transition-colors"
                      >
                        Use
                      </button>
                      <button
                        onClick={() => handleDeleteTemplate(t.id)}
                        disabled={deletingTemplateId === t.id}
                        className="p-1.5 text-zinc-500 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-colors disabled:opacity-50"
                        aria-label={`Delete template ${t.name}`}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Compose card */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 flex flex-col gap-5">
            <h2 className="font-semibold text-zinc-100">Compose</h2>

            {/* Title */}
            <div className="flex flex-col gap-1.5">
              <div className="flex justify-between">
                <label className="text-sm text-zinc-400">Title</label>
                <span className={`text-xs ${title.length > MAX_TITLE_CHARS ? 'text-red-400' : 'text-zinc-500'}`}>
                  {title.length} / {MAX_TITLE_CHARS}
                </span>
              </div>
              <input
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="e.g. Your weekly spending summary is ready"
                maxLength={MAX_TITLE_CHARS}
                className="bg-zinc-800 border border-zinc-700 rounded-xl px-4 py-2.5 text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-yellow-400/30 text-sm"
              />
            </div>

            {/* Body */}
            <div className="flex flex-col gap-1.5">
              <div className="flex justify-between">
                <label className="text-sm text-zinc-400">Message</label>
                <span className={`text-xs ${body.length > MAX_BODY_CHARS ? 'text-red-400' : 'text-zinc-500'}`}>
                  {body.length} / {MAX_BODY_CHARS}
                </span>
              </div>
              <textarea
                value={body}
                onChange={e => setBody(e.target.value)}
                placeholder="e.g. Open Hisab Kitab to review your transactions and stay on top of your budget."
                maxLength={MAX_BODY_CHARS}
                rows={3}
                className="bg-zinc-800 border border-zinc-700 rounded-xl px-4 py-2.5 text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-yellow-400/30 text-sm resize-none"
              />
            </div>

            {/* Target */}
            <div className="flex flex-col gap-2">
              <label className="text-sm text-zinc-400">Target</label>
              <div className="flex gap-3">
                <button
                  onClick={() => { setTargetType('all'); setUserSearch(''); setSelectedUserIds([]); }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm border transition-colors ${targetType === 'all' ? 'bg-yellow-400/10 border-yellow-400/40 text-yellow-400' : 'border-zinc-700 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800'}`}
                >
                  <Users size={14} /> All Users
                </button>
                <button
                  onClick={() => setTargetType('specific')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm border transition-colors ${targetType === 'specific' ? 'bg-yellow-400/10 border-yellow-400/40 text-yellow-400' : 'border-zinc-700 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800'}`}
                >
                  <Bell size={14} /> Specific Users
                </button>
              </div>
            </div>

            {/* Deep link screen */}
            <div className="flex flex-col gap-1.5">
              <label className="text-sm text-zinc-400">Deep link <span className="text-zinc-600">(optional)</span></label>
              <select
                value={screenSelection}
                onChange={e => setScreenSelection(e.target.value)}
                className="bg-zinc-800 border border-zinc-700 rounded-xl px-4 py-2.5 text-zinc-100 focus:outline-none focus:ring-2 focus:ring-yellow-400/30 text-sm"
              >
                <option value="">None (no deep link)</option>
                {deepLinkScreens.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
                <option value={playStoreSentinel}>Play Store listing</option>
                <option value={CUSTOM_URL_OPTION}>Custom URL…</option>
              </select>

              {screenSelection === CUSTOM_URL_OPTION && (
                <>
                  <input
                    type="text"
                    value={customUrl}
                    onChange={e => setCustomUrl(e.target.value)}
                    placeholder="https://your-org.youform.com/forms/xxxxx"
                    className={`bg-zinc-800 border rounded-xl px-4 py-2.5 text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-2 text-sm ${customUrlError ? 'border-red-500/60 focus:ring-red-500/30' : 'border-zinc-700 focus:ring-yellow-400/30'}`}
                  />
                  <p className="text-xs text-zinc-500">
                    Opens directly in the device browser — must start with{' '}
                    <span className="text-zinc-300">http://</span> or <span className="text-zinc-300">https://</span>.
                  </p>
                  {customUrlError && <p className="text-xs text-red-400">{customUrlError}</p>}
                </>
              )}

              {screenSelection && screenSelection !== CUSTOM_URL_OPTION && (
                <p className="text-xs text-zinc-500">
                  Tapping the notification will open:{' '}
                  <span className="text-zinc-300">
                    {screenSelection === playStoreSentinel
                      ? 'Play Store listing'
                      : deepLinkScreens.find(s => s.value === screenSelection)?.label}
                  </span>
                </p>
              )}
            </div>

            {/* User picker */}
            {targetType === 'specific' && (
              <div className="flex flex-col gap-2">
                <label className="text-sm text-zinc-400">
                  Select users {selectedUserIds.length > 0 && <span className="text-yellow-400">({selectedUserIds.length} selected)</span>}
                </label>
                <input
                  type="text"
                  placeholder="Search by name or email…"
                  value={userSearch}
                  onChange={e => setUserSearch(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-sm text-zinc-100 placeholder-zinc-500 focus:outline-none focus:border-yellow-400 transition-colors"
                />
                {usersLoading ? (
                  <p className="text-xs text-zinc-500">Loading users...</p>
                ) : (
                  <div className="max-h-48 overflow-y-auto flex flex-col gap-1 pr-1">
                    {allUsers.length === 0 ? (
                      <p className="text-xs text-zinc-500 px-3 py-2">No users found.</p>
                    ) : (
                      allUsers.map(u => (
                        <label
                          key={u.id}
                          className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer hover:bg-zinc-800 transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={selectedUserIds.includes(u.id)}
                            onChange={() => toggleUser(u.id)}
                            className="accent-yellow-400"
                          />
                          <span className="text-sm text-zinc-200">{u.display_name || u.email || u.id.slice(0, 8)}</span>
                          {u.display_name && <span className="text-xs text-zinc-500">{u.email}</span>}
                        </label>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Preview */}
            {(title || body) && (
              <div className="flex flex-col gap-1.5">
                <label className="text-sm text-zinc-400">Preview</label>
                <div className="bg-zinc-800 border border-zinc-700 rounded-xl p-4 flex gap-3 items-start">
                  <div className="w-8 h-8 rounded-lg bg-yellow-400/20 flex items-center justify-center flex-shrink-0">
                    <Bell size={14} className="text-yellow-400" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-zinc-100">{title || 'Title'}</p>
                    <p className="text-xs text-zinc-400 mt-0.5">{body || 'Message'}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Feedback */}
            {sendResult && (
              <div className="flex items-center gap-2 text-sm text-green-400 bg-green-400/10 border border-green-400/20 rounded-xl px-4 py-3">
                <CheckCircle2 size={15} />
                Sent to {sendResult.targetCount} device{sendResult.targetCount !== 1 ? 's' : ''} — {sendResult.successCount} accepted, {sendResult.failureCount} failed.
              </div>
            )}
            {sendError && (
              <div className="flex items-center gap-2 text-sm text-red-400 bg-red-400/10 border border-red-400/20 rounded-xl px-4 py-3">
                <XCircle size={15} /> {sendError}
              </div>
            )}

            {/* Send button */}
            {showConfirm ? (
              <div className="flex flex-col gap-3 bg-zinc-800 border border-zinc-700 rounded-xl p-4">
                <p className="text-sm text-zinc-300">
                  Send <span className="text-zinc-100 font-medium">"{title}"</span> to <span className="text-yellow-400 font-medium">{recipientCount}</span>?
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={handleSend}
                    disabled={sending}
                    className="flex items-center gap-2 px-5 py-2 bg-yellow-400 text-zinc-900 rounded-xl text-sm font-semibold hover:bg-yellow-300 disabled:opacity-50 transition-colors"
                  >
                    <Send size={14} />
                    {sending ? 'Sending...' : 'Confirm & Send'}
                  </button>
                  <button
                    onClick={() => setShowConfirm(false)}
                    className="px-5 py-2 text-sm text-zinc-400 hover:text-zinc-100 hover:bg-zinc-700 rounded-xl transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowConfirm(true)}
                disabled={!canSend}
                className="self-start flex items-center gap-2 px-5 py-2.5 bg-yellow-400 text-zinc-900 rounded-xl text-sm font-semibold hover:bg-yellow-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <Send size={14} /> Send Notification
              </button>
            )}
          </div>

          {/* History */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-zinc-100">History</h2>
              <span className="text-xs text-zinc-500">{total} total</span>
            </div>

            {historyLoading ? (
              <div className="text-zinc-500 text-sm">Loading...</div>
            ) : historyError ? (
              <div className="text-red-400 text-sm">{historyError}</div>
            ) : notifications.length === 0 ? (
              <div className="text-zinc-500 text-sm">No notifications sent yet.</div>
            ) : (
              <div className="flex flex-col gap-3">
                {notifications.map(n => (
                  <div key={n.id} className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5 flex flex-col gap-3">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex flex-col gap-0.5">
                        <p className="text-sm font-semibold text-zinc-100">{n.title}</p>
                        <p className="text-xs text-zinc-400">{n.body}</p>
                      </div>
                      <span className={`flex-shrink-0 text-xs px-2.5 py-1 rounded-full font-medium ${n.target_type === 'all' ? 'bg-zinc-700 text-zinc-300' : 'bg-yellow-400/10 text-yellow-400'}`}>
                        {n.target_type === 'all' ? 'All users' : 'Specific'}
                      </span>
                    </div>

                    <div className="flex flex-wrap gap-4 text-xs text-zinc-400">
                      <span className="flex items-center gap-1"><Users size={11} /> {n.target_count ?? '—'} targeted</span>
                      <span className="flex items-center gap-1 text-green-400"><CheckCircle2 size={11} /> {n.success_count ?? '—'} accepted</span>
                      {(n.failure_count ?? 0) > 0 && (
                        <span className="flex items-center gap-1 text-red-400"><XCircle size={11} /> {n.failure_count} failed</span>
                      )}
                      {n.delivered_count !== null && (
                        <span className="flex items-center gap-1 text-blue-400"><CheckCircle2 size={11} /> {n.delivered_count} delivered</span>
                      )}
                      {n.device_failure_count !== null && n.device_failure_count > 0 && (
                        <span className="flex items-center gap-1 text-orange-400"><XCircle size={11} /> {n.device_failure_count} device failures</span>
                      )}
                    </div>

                    {n.failure_summary && Object.keys(n.failure_summary).length > 0 && (
                      <div className="text-xs text-red-400/90 bg-red-950/20 border border-red-900/30 rounded-xl p-3 flex flex-col gap-1.5">
                        <span className="font-semibold text-zinc-300">Failure Reasons:</span>
                        <div className="flex flex-col gap-1">
                          {Object.entries(n.failure_summary).map(([reason, count]) => (
                            <div key={reason} className="flex justify-between items-center pl-2 border-l border-red-800">
                              <span>{reason}</span>
                              <span className="font-medium bg-red-950 px-1.5 py-0.5 rounded text-red-300">
                                {count} device{count !== 1 ? 's' : ''}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs text-zinc-500">Sent {formatDate(n.sent_at)} by {n.sent_by}</span>
                        {n.receipts_checked_at ? (
                          <span className="text-xs text-zinc-600">Receipts checked {formatDate(n.receipts_checked_at)}</span>
                        ) : (
                          <span className="flex items-center gap-1 text-xs text-zinc-600"><Clock size={10} /> Receipts not yet checked</span>
                        )}
                      </div>

                      <button
                        onClick={() => handleCheckReceipts(n.id)}
                        disabled={checkingReceiptId === n.id}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 border border-zinc-700 rounded-lg transition-colors disabled:opacity-50"
                      >
                        <RefreshCw size={11} className={checkingReceiptId === n.id ? 'animate-spin' : ''} />
                        {checkingReceiptId === n.id ? 'Checking...' : 'Check Receipts'}
                      </button>
                    </div>
                  </div>
                ))}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between pt-2">
                    <button
                      onClick={() => fetchHistory(page - 1)}
                      disabled={page <= 1}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm text-zinc-400 hover:text-zinc-100 disabled:opacity-30 transition-colors"
                    >
                      <ChevronLeft size={14} /> Prev
                    </button>
                    <span className="text-xs text-zinc-500">Page {page} of {totalPages}</span>
                    <button
                      onClick={() => fetchHistory(page + 1)}
                      disabled={page >= totalPages}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm text-zinc-400 hover:text-zinc-100 disabled:opacity-30 transition-colors"
                    >
                      Next <ChevronRight size={14} />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
